import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';

interface NewsletterSignupProps {
  variant?: 'inline' | 'modal' | 'sidebar';
  title?: string;
  description?: string;
}

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  variant = 'inline',
  title = 'Stay Connected to Cedar Falls',
  description = 'Get exclusive updates on new releases, behind-the-scenes content, and early access to Memory Keeper Mysteries.'
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    
    try {
      // Call the Cloudflare Worker API endpoint
      // Use direct URL in development since Vite dev server doesn't process _redirects
      const endpoint = import.meta.env.DEV 
        ? 'https://memory-keeper-newsletter.zack33.workers.dev'
        : '/api/newsletter';
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email,
          source: 'website'
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Welcome to the Memory Keeper community! Check your email for confirmation.');
        setEmail('');
      } else {
        throw new Error(data.error || 'Failed to subscribe');
      }
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
      
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again later.');
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <div className="w-5 h-5 animate-spin border-2 border-cedar-400 border-t-transparent rounded-full" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-forest-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-blood-400" />;
      default:
        return <Mail className="w-5 h-5 text-fog-400" />;
    }
  };

  const containerClasses = {
    inline: 'w-full',
    modal: 'max-w-md mx-auto',
    sidebar: 'w-full max-w-sm'
  };

  return (
    <div className={`${containerClasses[variant]} animate-fade-in-up`}>
      {/* Header */}
      <div className="text-center mb-4 sm:mb-6">
        <div className="flex justify-center mb-3 sm:mb-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-forest-600/20 rounded-full flex items-center justify-center animate-glow-pulse">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-forest-400" />
          </div>
        </div>
        <h3 className="text-lg sm:text-xl font-serif font-semibold text-fog-100 mb-2 px-2">
          {title}
        </h3>
        <p className="text-fog-400 text-sm leading-relaxed px-2">
          {description}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            disabled={status === 'loading'}
            className={`w-full px-4 py-3 bg-cedar-800/50 border rounded-lg text-fog-200 placeholder-fog-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-cedar-900 transition-all touch-manipulation min-h-[48px] text-base ${
              status === 'error' 
                ? 'border-blood-500 focus:ring-blood-500' 
                : status === 'success'
                ? 'border-forest-500 focus:ring-forest-500'
                : 'border-cedar-600 focus:ring-forest-500'
            }`}
          />
          {status !== 'idle' && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {getStatusIcon()}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-300 hover-lift ${
            status === 'success'
              ? 'bg-forest-600 text-white cursor-not-allowed'
              : status === 'error'
              ? 'bg-blood-600 text-white hover:bg-blood-500'
              : 'bg-forest-600 text-white hover:bg-forest-500'
          }`}
        >
          {status === 'loading' ? (
            <span className="flex items-center justify-center">
              <div className="w-4 h-4 mr-2 animate-spin border-2 border-white border-t-transparent rounded-full" />
              Joining...
            </span>
          ) : status === 'success' ? (
            <span className="flex items-center justify-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              Subscribed!
            </span>
          ) : (
            'Join the Memory Keeper Community'
          )}
        </button>

        {/* Status Message */}
        {message && (
          <div className={`p-3 rounded-lg text-sm animate-fade-in-up ${
            status === 'success' 
              ? 'bg-forest-600/20 text-forest-400 border border-forest-600/30' 
              : 'bg-blood-600/20 text-blood-400 border border-blood-600/30'
          }`}>
            {message}
          </div>
        )}
      </form>

      {/* Benefits */}
      <div className="mt-6 pt-6 border-t border-cedar-700/30">
        <p className="text-xs text-fog-500 mb-3 text-center">What you'll receive:</p>
        <ul className="space-y-2 text-xs text-fog-400">
          <li className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 bg-forest-400 rounded-full flex-shrink-0"></div>
            <span>New book release notifications</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 bg-forest-400 rounded-full flex-shrink-0"></div>
            <span>Exclusive behind-the-scenes content</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 bg-forest-400 rounded-full flex-shrink-0"></div>
            <span>Early access to excerpts and teasers</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 bg-forest-400 rounded-full flex-shrink-0"></div>
            <span>Cedar Falls world-building insights</span>
          </li>
        </ul>
        <p className="text-xs text-fog-500 mt-4 text-center">
          No spam, unsubscribe anytime. Your email stays in Cedar Falls.
        </p>
      </div>
    </div>
  );
};